import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify'
import { useAppContext } from '../../../AppContext';
import mainAxios from '../../../services/mainAxios';
import { useTranslation } from 'react-i18next';
import Loader from '../../Layout/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCancel, faCheck, faCheckCircle, faCog, faEdit, faFileUpload, faMapMarkerAlt, faPlus, faTimes, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { Preloader, ThreeDots } from '../../global/Preloader';
import func from '../../../services/functions';
import { useDropzone } from 'react-dropzone';
import moment from 'moment';
import EditCompanyCertificate from '../EditCompanyCertificate';
import selectStyle from '../../../design/selectStyle'
import Select from "react-select"
import TooltipOverlay from '../../global/TooltipOverlay';


const Company = ({ company, setCompany, handleCompanyInputs, filePreview, setFilePreview, getCompany, getLogo, logoLoading, editCompany, setPageLoading, isAdminMode }) => {
  const {
    user: [user],
    selectedCompany: [selectedCompany]
  } = useAppContext();
  const { t, i18n } = useTranslation("translations");
  const [oldState, setOldState] = useState()
  const [isEditable, setIsEditable] = useState(false)
  const [loading, setLoading] = React.useState(false)
  const isRoleDisabled = user?.role?.id != 1
  // In admin mode (managing a company via /settings/general-info?companyId=...),
  // preserve the companyId on quick-action links so the target page and its
  // breadcrumb keep managing the same company instead of the navbar selection.
  const adminQuery = isAdminMode && company?.id ? `?companyId=${company.id}` : ""
  const certificateExpired = !!company?.certificateExpirationDate
    && moment(company.certificateExpirationDate).isBefore(moment())
  const fileContainer = useRef();
  const [visibleCertificate, setVisibleCertificate] = useState(false);


  const FILE_SIZE_LIMIT = 1000 * 1024;

  const handleEditForm = () => {
    setIsEditable(!isEditable)
    setOldState({ ...company })
  }

  const companyCategories = [
    { label: t("invoice.select"), value: "" },
    { label: t("company.construction"), value: "Kompany ndertimi" },
    { label: t("company.insurance"), value: "Siguracione" },
    { label: t("company.health"), value: "Shendetesore" },
    { label: t("company.accounting"), value: "Studio Kontabel" },
    { label: "Bank", value: "Banke" },
    { label: t("company.educational"), value: "Institucione Arsimore" },
    { label: "OJF", value: "OJF" },
    { label: t("company.stateInst"), value: "Institucione shteterore" },
    { label: t("invoice.other"), value: "Tjeter" },
  ]

  const cancelCompanyEdit = () => {
    setCompany({ ...oldState });
    setIsEditable(false)
  }

  const removeCompanyLogo = async () => {
    try {
      const res = await mainAxios.post("/apiEndpoint/saveOrUpdate", {
        object: "DeleteAndReplaceCompanyLogo",
        nuis: user.companiesRights.find(
          (company) => company.id === parseInt(selectedCompany)
        )?.nuis,
        username: user?.username,
        content: {
          id: company?.id,
          nuis: company?.nuis,
          name: company?.name,
        },
      });

      if (res?.data?.status === true) {
        toast.success(t("company.logoRemoved"), { containerId: "all_toast" });
        setFilePreview(null);
        getCompany({ reload: true });
        getLogo(company);

      } else {
        toast.error(t("company.logoError"), { containerId: "all_toast" });
      }
    } catch (err) {
      console.error("Error removing logo:", err);
      toast.error(t("company.logoError"), { containerId: "all_toast" });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ".png,.jpg,.jpeg",
    onDrop: async (files) => {
      let filesExceeded = 0;
      for (const file of files) {
        const extension =
          file && file.name.split(".")[file.name.split(".").length - 1];

        if (
          extension &&
          (extension === "png" || extension === "jpg" || extension === "jpeg")
        ) {
          let base64File = await func.toBase64(file);
          if (base64File.length * (3 / 4) < FILE_SIZE_LIMIT) {
            setFilePreview({
              base64: base64File.substr(base64File.indexOf(",") + 1),
              name: file.name,
              extension: extension,
            });
          } else filesExceeded += 1;
        }
      }
      if (filesExceeded) {
        toast.warning(t("company.imageLimit"), { containerId: "all_toast" });
      }
    },
  });


  const closePreview = () => {
    setTimeout(() => {
      setFilePreview(null);
    }, 150);
    fileContainer.current.classList.toggle("open");
  };

  const saveLogo = () => {
    mainAxios
      .post("apiEndpoint/saveOrUpdate", {
        object: "UploadCompanyLogo",
        nuis: company?.nuis,
        content: { file: filePreview.base64 },
      })
      .then((res) => {
        if (res?.data?.status === true) {
          toast.success(t("company.logoUploaded"), { containerId: "all_toast" });
          getCompany({ reload: true });
          setFilePreview((state) => ({ ...state, uploaded: true }));
        } else {
          setFilePreview(null);
        }
      });
  };


  return (
    <div className="company-wrapper">
      {/* Top Header with Actions */}
      <div className="company-top-header">
        <div className="company-top-header__left">
          <h2 className="company-top-header__title">{company.name}</h2>
          <span className="company-top-header__nipt">{company.nuis}</span>
        </div>
        <div className="company-top-header__actions">
          {isEditable ? (
            <>
              <button
                onClick={async () => {
                  setLoading(true)
                  const edited = await editCompany()
                  if (edited) setIsEditable(false)
                  setLoading(false)
                }}
                className="company-btn company-btn--save"
                disabled={loading}
              >
                {loading ? <Loader size="xs" color="white" /> : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {t("common.save")}
                  </>
                )}
              </button>
              <button
                className="company-btn company-btn--cancel"
                onClick={cancelCompanyEdit}
              >
                <FontAwesomeIcon icon={faCancel} />
                {t("common.cancel")}
              </button>
            </>
          ) : (
            <button
              className="company-btn company-btn--edit"
              onClick={handleEditForm}
            >
              <FontAwesomeIcon icon={faEdit} />
              {t("company.editCompany")}
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="company-main">
        <div className="company-main__grid">
          {/* Left Column - Basic Information */}
          <div className="company-main__left">
            {/* Logo Upload Section */}
            <div className="company-logo-section">
              <div className="company-logo-section__upload" {...getRootProps()}>
                <input {...getInputProps()} disabled={logoLoading} />
                {logoLoading && (
                  <div className="custom-loading">
                    <Preloader
                      use={ThreeDots}
                      size={45}
                      strokeWidth={6}
                      strokeColor="#007567"
                      duration={1000}
                    />
                  </div>
                )}
                {!filePreview && !logoLoading && (
                  <div className="company-logo-section__placeholder">
                    <FontAwesomeIcon
                      icon={faFileUpload}
                      size="2x"
                    />
                    <span>Logo</span>
                  </div>
                )}
                {filePreview && !logoLoading && (
                  <div ref={fileContainer} className="company-logo-section__preview">
                    <img
                      src={func.b64toURL(
                        filePreview.base64,
                        "image/*",
                        filePreview.name
                      )}
                      alt="Company Logo"
                    />
                  </div>
                )}
              </div>
              {/* Logo Action Buttons - Next to image */}
              <div className="company-logo-section__actions">
                {filePreview && !filePreview?.uploaded && (
                  <>
                    <button onClick={saveLogo} className="company-btn company-btn--save company-btn--sm">
                      <FontAwesomeIcon icon={faCheck} />
                      {t("common.save")}
                    </button>
                    <button onClick={closePreview} className="company-btn company-btn--cancel company-btn--sm">
                      <FontAwesomeIcon icon={faTimes} />
                      {t("common.cancel")}
                    </button>
                  </>
                )}
                {filePreview && filePreview?.uploaded && !company?.logoPath?.includes("/profisc_logo.PNG") && (
                  <button onClick={removeCompanyLogo} className="company-btn company-btn--delete company-btn--sm">
                    <FontAwesomeIcon icon={faTimes} />
                    {t("company.removeLogo")}
                  </button>
                )}
              </div>
            </div>

            <div className="company-info-section">
              <h3 className="company-info-section__title">INFORMACIONI BAZË</h3>

              <div className="company-info-row">
                <div className="company-info-row__col">
                  <label className="company-info-row__label">{t('company.name')}</label>
                  {isEditable ? (
                    <input
                      type="text"
                      className="company-input"
                      name="name"
                      disabled={isRoleDisabled}
                      value={company.name}
                      onChange={handleCompanyInputs}
                    />
                  ) : (
                    <p className="company-info-row__value">{company.name || '-'}</p>
                  )}
                </div>
                <div className="company-info-row__col">
                  <label className="company-info-row__label">NIPT</label>
                  {isEditable ? (
                    <input
                      type="text"
                      className="company-input"
                      name="nuis"
                      disabled={isRoleDisabled}
                      value={company.nuis}
                      onChange={handleCompanyInputs}
                    />
                  ) : (
                    <p className="company-info-row__value">{company.nuis || '-'}</p>
                  )}
                </div>
              </div>

              <div className="company-info-row">
                <div className="company-info-row__col">
                  <label className="company-info-row__label">Email</label>
                  {isEditable ? (
                    <input
                      type="text"
                      className="company-input"
                      name="email"
                      value={company.email || ''}
                      onChange={handleCompanyInputs}
                    />
                  ) : (
                    <p className="company-info-row__value">{company.email || '-'}</p>
                  )}
                </div>
                <div className="company-info-row__col">
                  <label className="company-info-row__label">{t("users.phone")}</label>
                  {isEditable ? (
                    <input
                      type="text"
                      className="company-input"
                      name="phoneNumber"
                      value={company.phoneNumber || ''}
                      onChange={handleCompanyInputs}
                    />
                  ) : (
                    <p className="company-info-row__value">{company.phoneNumber || '-'}</p>
                  )}
                </div>
              </div>

              <div className="company-info-row">
                <div className="company-info-row__col">
                  <label className="company-info-row__label">{t("customers.address")}</label>
                  {isEditable ? (
                    <input
                      type="text"
                      className="company-input"
                      name="companyAddress"
                      value={company.companyAddress || ''}
                      onChange={handleCompanyInputs}
                    />
                  ) : (
                    <p className="company-info-row__value">{company.companyAddress || '-'}</p>
                  )}
                </div>
              </div>

              <div className="company-info-row">
                <div className="company-info-row__col">
                  <label className="company-info-row__label">{t("company.websiteUrl")}</label>
                  {isEditable ? (
                    <input
                      type="text"
                      className="company-input"
                      name="websiteUrl"
                      value={company.websiteUrl || ''}
                      onChange={handleCompanyInputs}
                    />
                  ) : (
                    company?.websiteUrl ? (
                      <a
                        href={company?.websiteUrl.startsWith('http') ? company?.websiteUrl : `https://${company?.websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="company-info-row__link"
                      >
                        {company?.websiteUrl}
                      </a>
                    ) : (
                      <p className="company-info-row__value">-</p>
                    )
                  )}
                </div>
                <div className="company-info-row__col">
                  <label className="company-info-row__label">{t("common.companyCategory")}</label>
                  {isEditable ? (
                    <Select
                      styles={selectStyle}
                      name="companyCategory"
                      value={companyCategories.find((el) => el.value === company?.companyCategory)}
                      options={companyCategories}
                      placeholder={t("invoice.select")}
                      menuPlacement="top"
                      onChange={(option) => handleCompanyInputs(null, "companyCategory", option.value)}
                    />
                  ) : (
                    <p className="company-info-row__value">{company?.companyCategory || '-'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Info Fiskale Box with Certificate */}
          <div className="company-main__right">
            <div className="company-fiskale-box">
              <h3 className="company-fiskale-box__title">INFO FISKALE</h3>

              <div className="company-fiskale-box__item">
                <p className="company-fiskale-box__label">Kompania Aktive</p>
                {isEditable ? (
                  <div className="company-status-chips">
                    <button
                      type="button"
                      className={`company-status-chip ${company.active === true || company.active === 'true' ? 'company-status-chip--active' : ''}`}
                      onClick={() => !isRoleDisabled && handleCompanyInputs({ target: { name: 'active', value: true } })}
                      disabled={isRoleDisabled}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      {t('company.yes')}
                    </button>
                    <button
                      type="button"
                      className={`company-status-chip ${company.active === false || company.active === 'false' ? 'company-status-chip--inactive' : ''}`}
                      onClick={() => !isRoleDisabled && handleCompanyInputs({ target: { name: 'active', value: false } })}
                      disabled={isRoleDisabled}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                      {t('company.no')}
                    </button>
                  </div>
                ) : (
                  <p className={`company-fiskale-box__value ${company?.active ? 'active' : 'inactive'}`}>
                    {company?.active ? t('company.yes') : t('company.no')}
                  </p>
                )}
              </div>

              <hr className="company-fiskale-box__divider" />

              <div className="company-fiskale-box__item">
                <p className="company-fiskale-box__label">{t("home.date")}</p>
                <p className="company-fiskale-box__value">
                  {moment(company.recordDate).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>

              <hr className="company-fiskale-box__divider" />

              {/* Certificate Section */}
              <div className="company-fiskale-box__item">
                <p className="company-fiskale-box__label">{t("company.certificate")}</p>
                <div className="company-fiskale-box__certificate">
                  <div className="company-fiskale-box__cert-details">
                    {!company.hasCertificate ? (
                      <span className="company-fiskale-box__cert-status company-fiskale-box__cert-status--inactive">
                        {t("company.certificateMissing")}
                      </span>
                    ) : certificateExpired ? (
                      <span className="company-fiskale-box__cert-status company-fiskale-box__cert-status--inactive">
                        {t("subscription.expired")}
                      </span>
                    ) : (
                      <span className="company-fiskale-box__cert-status company-fiskale-box__cert-status--active">
                        <FontAwesomeIcon icon={faCheckCircle} /> Aktiv
                      </span>
                    )}
                    {company?.certificateExpirationDate && (
                      <span className="company-fiskale-box__cert-expiration">
                        <span>{t("company.certificateValidTo")}</span>
                        <strong>{moment(company.certificateExpirationDate).format("DD/MM/YYYY HH:mm")}</strong>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setVisibleCertificate(true)}
                    className="company-btn company-btn--sm company-btn--outline"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("subscription.add")}
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Actions - Horizontal */}
            <div className="company-quick-actions">
              <h4 className="company-quick-actions__title">{t("company.quickActions")}</h4>
              <div className="company-quick-actions__list">
                <Link to={`/settings/subscription${adminQuery}`} className="company-quick-actions__item">
                  <FontAwesomeIcon icon={faCreditCard} className="company-quick-actions__icon" />
                  <span>{t("navbar.subscriptions")}</span>
                </Link>
                <Link to={`/settings/branches${adminQuery}`} className="company-quick-actions__item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="company-quick-actions__icon" />
                  <span>{t("company.branches")}</span>
                </Link>
                <Link to={`/settings/preferences${adminQuery}`} className="company-quick-actions__item">
                  <FontAwesomeIcon icon={faCog} className="company-quick-actions__icon" />
                  <span>{t("settings.preferences")}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditCompanyCertificate
        company={company}
        setCompany={setCompany}
        visibleCertificate={visibleCertificate}
        setVisibleCertificate={setVisibleCertificate}
      />
    </div>
  )
}

export default Company
